import React from 'react';
import { FetchResult } from '@apollo/client';
import classnames from 'classnames';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import ArticlePreview from 'buyside/components/presentation/Feed/ArticlePreview/articlePreview';
import CardArea from 'buyside/components/presentation/CardArea/cardArea';
import CommentBodyRenderer from 'buyside/components/presentation/CommentBodyRenderer/commentBodyRenderer';
import Icon from 'buyside/components/presentation/Icon/icon';
import Like from 'buyside/components/presentation/Feed/Actions/like';
import Link from 'buyside/components/presentation/Link/link';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';
import SocialMediaShareButtons from 'buyside/components/presentation/SocialMediaShareButtons/socialMediaShareButtons';
import YouTubePlayer from 'buyside/components/presentation/YouTubePlayer/youtubePlayer';

import MessageBubbleIcon from 'buyside/svgs/message_bubble.svg';

import {
  defaultUserImageUrl,
  employerProfileUrl,
  postUrl,
  postsSearchUrl,
  userUrl
} from 'buyside/util/urlHelper';

import {
  EmployerHistory,
  Employer,
  Maybe,
  Post,
  Profile,
  Rank,
  User,
  usePostIndexCardQuery,
  useLikeEventMutation,
  LikeEventMutation
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/PostIndex/postIndexStyles.scss';

export type PostNodeType = Pick<
  Post,
  | 'body'
  | 'commentsCount'
  | 'domain'
  | 'eventId'
  | 'id'
  | 'likeCount'
  | 'ogDescription'
  | 'ogUrl'
  | 'ogPreviewImageUrl'
  | 'ogTitle'
  | 'tagList'
  | 'untaggedBody'
> & {
  user?: Maybe<
    Pick<User, 'id' | 'displayName' | 'profileImageUrl'> & {
      featuredRank?: Maybe<Pick<Rank, 'id' | 'rankString'>>;
      mostRecentEmployer?: Maybe<
        Pick<EmployerHistory, 'currentEmployer' | 'id' | 'title'> & {
          employer?: Maybe<Pick<Employer, 'aumRange' | 'fundType' | 'id' | 'name'>>;
        }
      >;
      profile?: Maybe<Pick<Profile, 'city' | 'id'>>;
    }
  >;
};

export type PostUnindexedAttr = {
  currentUserHasLiked?: boolean;
  postedAt?: Maybe<string>;
  youtubeId?: Maybe<string>;
};

const PostIndexCard: React.FC<PostNodeType &
  PostUnindexedAttr & {
    isAuthor: boolean;
    isIndex: boolean;
    showUserDetails: boolean;
  }> = ({
  body,
  commentsCount,
  currentUserHasLiked,
  domain,
  eventId,
  id,
  isAuthor,
  isIndex,
  likeCount,
  ogDescription,
  ogUrl,
  ogPreviewImageUrl,
  ogTitle,
  postedAt,
  showUserDetails,
  tagList,
  untaggedBody,
  user,
  youtubeId
}) => {
  const { data } = usePostIndexCardQuery({ skip: !isIndex, variables: { id } });

  const [likePost] = useLikeEventMutation({
    update(cache, { data }) {
      const hasLiked = Boolean(data?.likeEvent?.event?.currentUserHasLiked);

      cache.modify<Post>({
        id: cache.identify({ id: id, __typename: 'Post' }),
        fields: {
          currentUserHasLiked: () => hasLiked,
          likeCount: cachedLikeCount => (hasLiked ? cachedLikeCount + 1 : cachedLikeCount - 1)
        }
      });
    }
  });

  const videoId = youtubeId || data?.post?.youtubeId;

  return (
    <CardArea>
      <div className={styles.postCardContainer}>
        <div className={styles.topContainer}>
          <ProfileImage
            source={
              showUserDetails
                ? user?.profileImageUrl || defaultUserImageUrl()
                : defaultUserImageUrl()
            }
            size={IMAGE_SIZES.SMALLER}
          />
          <div className={styles.userContainer}>
            {showUserDetails ? (
              <>
                <Link className={styles.blackLink} to={userUrl(user?.id)}>
                  {user?.displayName}
                </Link>
                {user?.featuredRank && (
                  <>
                    {' '}
                    | <span className={styles.userDetails}>{user.featuredRank.rankString}</span>
                  </>
                )}
                {isIndex && (
                  <Link
                    className={styles.authorFilter}
                    to={postsSearchUrl(`author_name=${user?.displayName}`)}
                  >
                    More by this member
                  </Link>
                )}
                {user?.mostRecentEmployer?.employer && (
                  <div className={styles.userDetails}>
                    {user.mostRecentEmployer.currentEmployer ? '' : 'Formerly '}
                    {user.mostRecentEmployer.title ? `${user.mostRecentEmployer.title} at ` : ''}
                    <Link
                      className={classnames(styles.lightFont, styles.blackLink)}
                      to={employerProfileUrl(user.mostRecentEmployer.employer.id)}
                    >
                      {user.mostRecentEmployer.employer.name}
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <>
                <AnonymousUserDisplay anonUser={{ title: user?.mostRecentEmployer?.title }} />
                {user?.featuredRank && (
                  <>
                    {' '}
                    | <span className={styles.userDetails}>{user.featuredRank.rankString}</span>
                  </>
                )}
                {user?.mostRecentEmployer?.employer && (
                  <AnonEmployerAssociation
                    aumRange={user.mostRecentEmployer.employer?.aumRange || null}
                    fundType={user.mostRecentEmployer.employer?.fundType || null}
                    profileCity={user.profile?.city || null}
                  />
                )}
              </>
            )}
          </div>
          <div className={styles.posted}>{postedAt || data?.post?.postedAt} ago</div>
        </div>

        <div className={styles.postBody}>
          <CommentBodyRenderer body={body} />
        </div>

        {tagList?.length ? (
          <div className={styles.tagSection}>
            {tagList.map((tag, idx) => (
              <Link className={styles.tag} key={idx} to={postsSearchUrl(`tag=${tag}`)}>
                {`#${tag}`}
              </Link>
            ))}
          </div>
        ) : null}
        {videoId ? (
          <YouTubePlayer width={640} height={390} videoId={videoId} />
        ) : (
          <ArticlePreview
            domain={domain}
            ogDescription={ogDescription}
            ogUrl={ogUrl || ''}
            ogPreviewImageUrl={ogPreviewImageUrl}
            ogTitle={ogTitle}
          />
        )}
        <div className={styles.bottomContainer}>
          <div className={styles.userEngagement}>
            {eventId && (
              <Like
                currentUserHasLiked={
                  Boolean(currentUserHasLiked) || Boolean(data?.post?.currentUserHasLiked)
                }
                id={`post-like-${id}`}
                likeCount={likeCount}
                onClick={(): Promise<FetchResult<LikeEventMutation>> =>
                  likePost({ variables: { eventId } })
                }
              />
            )}
            <Link to={postUrl(id)}>
              <Icon type={MessageBubbleIcon} style={styles.bubble} />
              {commentsCount || 0} comments
            </Link>
            {isAuthor && (
              <SocialMediaShareButtons.TwitterButton
                shareText={untaggedBody || ''}
                shareUrl={`https://sumzero.com${postUrl(id)}`}
                size="sm"
              />
            )}
          </div>
          <Link to={postUrl(id)}>Read More</Link>
        </div>
      </div>
    </CardArea>
  );
};

export default PostIndexCard;
